/**
 * The safety boards' registers, read from the platform, and the forms the
 * safety board opens.
 *
 * Renders only the form a board asked for. It sits in the shell because the
 * safety board is reached from /sms and from the overview's own navigation,
 * and the incident panels from half a dozen dashboards.
 *
 * Reading, like FlightsFeed:
 *
 *   - Nothing is asked for until a board's engine has loaded — the only
 *     reader of what this fills — and never for a register this role may not
 *     read: that one is emptied to this browser's own entries and the board
 *     says why, rather than a 403 every minute.
 *   - On the platform, the registers replace the sample (mergeSafety).
 *   - Under the sample the board keeps its sample exactly as seeded until
 *     somebody writes to the sample's register; from then on it reads that
 *     register too, so what was just filed is what the board shows.
 *   - The pack's matrix and the security categories are vocabulary, not
 *     records: they reach the board under both sources as soon as it loads.
 */
import { lazy, Suspense, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useSession } from "../../../app/SessionContext";
import { DataSourceError, hasPermission, PERMISSION } from "../../../api/dataSource";
import {
  useAnomalyAcks,
  useHazards,
  useSafetyReports,
  useSafetyVocabulary,
  useSecurityIncidents,
  useSecurityVocabulary,
} from "../../../api/safetyQueries";
import { mockSafetyVersion, subscribeMockSafety } from "../../../mock/safety";
import { t } from "../../../i18n";
import { demoIfLoaded, whenDemoLoaded } from "../../domain/load";
import { HAZARD_ACTS, type HazardAct, type SafetyOpen, type SmsHost } from "../../../features/safety/host";
import type { Part, SafetyFeedInput } from "../mergeSafety";

const merger = () => import("../mergeSafety");
const Dialogs = lazy(() => import("../../../features/safety/SafetyDialogs"));

/** One register's answer for the merge: rows, a refusal, or not yet. */
export function partOf<T, R>(
  q: Pick<UseQueryResult<T>, "data" | "error">,
  permitted: boolean,
  permission: string,
  pick: (d: T) => R,
): Part<R> {
  if (!permitted) return { refused: t("your role does not hold {p}", { p: permission }) };
  if (q.error instanceof DataSourceError && q.error.status === 403) return { refused: q.error.message };
  if (q.data !== undefined) return { rows: pick(q.data) };
  return undefined;
}

export function SafetyFeed() {
  const { source, principal } = useSession();
  const live = source.kind === "platform";
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);
  useEffect(() => {
    if (!engine) whenDemoLoaded(() => setEngine(true));
  }, [engine]);
  const written = useSyncExternalStore(subscribeMockSafety, mockSafetyVersion, mockSafetyVersion);
  const on = engine && (live || written > 0);

  const can = (k: string) => hasPermission(principal, k);
  const read = can(PERMISSION.safetyRead);
  const reportRead = can(PERMISSION.safetyReportRead);
  const securityRead = can(PERMISSION.securityRead);
  const siteRead = can(PERMISSION.siteRead);

  const vocab = useSafetyVocabulary({ enabled: engine && read });
  const categories = useSecurityVocabulary({ enabled: engine && securityRead });
  const hazards = useHazards({ enabled: on && read });
  const reports = useSafetyReports({ enabled: on && reportRead });
  const incidents = useSecurityIncidents({ enabled: on && securityRead });
  const acks = useAnomalyAcks({ enabled: on && siteRead });
  const canRestricted = can(PERMISSION.securityReadRestricted);

  const input = useMemo((): SafetyFeedInput | null => {
    if (!engine) return null;
    const base: SafetyFeedInput = {
      live,
      vocabulary: vocab.data,
      categories: categories.data,
      canRaiseRestricted: canRestricted,
    };
    if (!on) return vocab.data || categories.data ? base : null;
    return {
      ...base,
      hazards: partOf(hazards, read, PERMISSION.safetyRead, (d) => d.items),
      reports: partOf(reports, reportRead, PERMISSION.safetyReportRead, (d) => d.items),
      incidents: partOf(incidents, securityRead, PERMISSION.securityRead, (d) => d),
      acks: partOf(acks, siteRead, PERMISSION.siteRead, (d) => d),
    };
    // the query objects change identity every render; their data and errors are what matter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    engine,
    on,
    live,
    read,
    reportRead,
    securityRead,
    siteRead,
    canRestricted,
    vocab.data,
    categories.data,
    hazards.data,
    hazards.error,
    reports.data,
    reports.error,
    incidents.data,
    incidents.error,
    acks.data,
    acks.error,
  ]);

  useEffect(() => {
    if (!input) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeSafety(input)));
  }, [input]);

  return <SafetyHost />;
}

/**
 * The page the safety board delegates its forms to (the engine's SMS_HOST).
 * Registered once the engine is here, and taken back when the shell goes.
 */
function SafetyHost() {
  const [open, setOpen] = useState<SafetyOpen | null>(null);

  useEffect(() => {
    let gone = false;
    const host: SmsHost = {
      fileReport: ({ hazard }) => setOpen({ kind: "file", hazard: hazard ?? null }),
      openReport: (ref) => setOpen({ kind: "report", ref }),
      raiseHazard: () => setOpen({ kind: "raise" }),
      hazard: (ref, act) => {
        if ((HAZARD_ACTS as readonly string[]).includes(act)) setOpen({ kind: "hazard", ref, act: act as HazardAct });
      },
    };
    whenDemoLoaded((d) => {
      if (gone) return;
      d.onSmsHost(host);
      /* the board's buttons depend on whether a page is listening */
      try {
        d.dashDraw();
      } catch {
        /* no dashboard board is mounted */
      }
    });
    return () => {
      gone = true;
      demoIfLoaded()?.onSmsHost(null);
    };
  }, []);

  if (!open) return null;
  return (
    <Suspense fallback={null}>
      {/* a key per form, so moving from one to the next starts it fresh */}
      <Dialogs key={JSON.stringify(open)} open={open} onClose={() => setOpen(null)} onOpen={setOpen} />
    </Suspense>
  );
}
